import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import format from 'date-fns/format';
import { format as timezoneFormat } from 'date-fns-tz';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { showPublishSuccessFlags } from '@confluence/editor-features/entry-points/utils/showPublishSuccessFlags.tsx';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';

import type {
	ContentSchedulePublishQuery$data,
	ContentSchedulePublishQuery$variables,
} from './__generated__/ContentSchedulePublishQuery.graphql.ts';
import { ContentSchedulePublishQuery } from './ContentSchedulePublishQuery.graphql.ts';
export const i18n = defineMessages({
	rescheduleUpdateWithSpaceName: {
		id: 'editor-schedule-publish.success.reschedule.update.with.space.name',
		defaultMessage:
			'{pageTitle} has been rescheduled to update on {date} at {time} in the {spaceName} space',
		description:
			"A message that displays in a flag when a page's scheduled update is successfully rescheduled, where the page is planned to be updated in a new space",
	},
	rescheduleUpdate: {
		id: 'editor-schedule-publish.success.reschedule.update',
		defaultMessage: '{pageTitle} has been rescheduled to update on {date} at {time}',
		description:
			"A message that displays in a flag when a page's scheduled update is successfully rescheduled",
	},
	reschedulePublishWithSpaceName: {
		id: 'editor-schedule-publish.success.reschedule.publish.with.space.name',
		defaultMessage:
			'{pageTitle} has been rescheduled to publish on {date} at {time} in the {spaceName} space',
		description:
			"A message that displays in a flag when a page's scheduled publish is successfully rescheduled, where the page is planned to be published in a new space",
	},
	reschedulePublish: {
		id: 'editor-schedule-publish.success.reschedule.publish',
		defaultMessage: '{pageTitle} has been rescheduled to publish on {date} at {time}',
		description:
			"A message that displays in a flag when a page's scheduled publish is successfully rescheduled",
	},
	scheduleUpdateWithSpaceName: {
		id: 'editor-schedule-publish.success.schedule.update.message.with.space.name',
		defaultMessage: '{pageTitle} will be updated on {date} at {time} in the {spaceName} space',
		description:
			'A message that displays in a flag when a page is scheduled to be updated and moved to a new space',
	},
	scheduleUpdate: {
		id: 'editor-schedule-publish.success.schedule.update.message',
		defaultMessage: '{pageTitle} will be updated on {date} at {time}',
		description: 'A message that displays in a flag when a page is scheduled to be updated',
	},
	schedulePublishWithSpaceName: {
		id: 'editor-schedule-publish.success.schedule.publish.message.with.space.name',
		defaultMessage: '{pageTitle} will be published on {date} at {time} in the {spaceName} space',
		description:
			'A message that displays in a flag when a page is scheduled to be published and moved to a new space',
	},
	schedulePublish: {
		id: 'editor-schedule-publish.success.schedule.publish.message',
		defaultMessage: '{pageTitle} will be published on {date} at {time}',
		description: 'A message that displays in a flag when a page is scheduled to be published',
	},
	scheduleUpdateTitle: {
		id: 'editor-schedule-publish.success.schedule.update.title',
		defaultMessage: 'Update scheduled',
		description:
			"A title for a flag that displays when a page's scheduled update is scheduled or rescheduled",
	},
	schedulePublishTitle: {
		id: 'editor-schedule-publish.success.schedule.publish.title',
		defaultMessage: 'Publish scheduled',
		description:
			"A title for a flag that displays when a page's scheduled publish is scheduled or rescheduled",
	},
});

type SchedulePublishFlagsWatcherProps = {
	contentId: string;
	contentType: string;
	title: string | null;
	isUnpublishedDraft: boolean;
	flags: FlagsStateContainer;
	isSchedulePublishEnabled: boolean;
};

type FlagDataType = {
	spaceName?: string;
	title: string | null;
	isUnpublishedDraft: boolean;
	currentlyScheduledPublishDate: Date | null;
};

// This hook is used to show a flag to users when another user schedules a publish.
export const useSchedulePublishFlagsWatcher = ({
	contentId,
	contentType,
	title,
	isUnpublishedDraft,
	flags,

	// TODO MODES-3970 isSchedulePublishEnabled should be removed when when its consumer(FullPageEditorComponent) doesn't have to support the use case of schedule publish being disabled.
	// This is set to true when on a live page. Live pages currently use FullPageEditorComponent and do not support scheduling publishes.
	isSchedulePublishEnabled = true,
}: SchedulePublishFlagsWatcherProps) => {
	const [currentlyScheduledPublishDate, setCurrentlyScheduledPublishDate] = useState<Date | null>(
		null,
	);
	const [spaceName] = useState();
	const intl = useIntl();
	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [getContent, { data: contentData }] = useLazyQueryInterop<
		ContentSchedulePublishQuery$data,
		ContentSchedulePublishQuery$variables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		ContentSchedulePublishQuery,
		{
			fetchPolicy: 'network-only',
		},
	);

	/**
	 * Store refs to state values used in handleSchedulePublish, because putting them
	 * directly into the callback causes the memoized function to be recreated several
	 * times rapidly, resulting in issues with PubSub subscribe/unsubscribing properly
	 */
	const flagDataRef = useRef<FlagDataType>({
		spaceName,
		title,
		isUnpublishedDraft,
		currentlyScheduledPublishDate,
	});

	/**
	 * Returns the object received by intl.formatMessage for the schedule publish success flag's content
	 */
	const getFlagMessage = useCallback(() => {
		const updating = !flagDataRef.current.isUnpublishedDraft;
		if (flagDataRef.current.currentlyScheduledPublishDate) {
			// Rescheduling
			if (updating) {
				if (flagDataRef.current.spaceName) {
					return i18n.rescheduleUpdateWithSpaceName;
				}
				return i18n.rescheduleUpdate;
			} else {
				// Publishing
				if (flagDataRef.current.spaceName) {
					return i18n.reschedulePublishWithSpaceName;
				}
				return i18n.reschedulePublish;
			}
		} else {
			// Scheduling (for first time)
			if (updating) {
				if (flagDataRef.current.spaceName) {
					return i18n.scheduleUpdateWithSpaceName;
				}
				return i18n.scheduleUpdate;
			} else {
				// Publishing
				if (flagDataRef.current.spaceName) {
					return i18n.schedulePublishWithSpaceName;
				}
				return i18n.schedulePublish;
			}
		}
	}, []);

	const handleSchedulePublish = useCallback(
		(_delta: string, response: any) => {
			const { eventType, publishTime } = response?.schedulePublished || {};
			const { accountId } = response;
			if (!isSchedulePublishEnabled) {
				return;
			}

			switch (eventType) {
				case 'scheduled':
					const scheduledPublishDate = new Date(publishTime);
					const monthDayRegex = /[^,]*$/g; // selects everything after comma ","
					const updating = !flagDataRef.current.isUnpublishedDraft;

					void flags
						.showFlag({
							type: 'success-circle',
							close: 'auto',
							title: intl.formatMessage(
								updating ? i18n.scheduleUpdateTitle : i18n.schedulePublishTitle,
							),
							description: intl.formatMessage(getFlagMessage(), {
								pageTitle: <b>{flagDataRef.current.title}</b>,
								date: format(scheduledPublishDate, 'PP').replace(monthDayRegex, ''),
								time: timezoneFormat(scheduledPublishDate, 'h:mm aaaa z'),
								spaceName: flagDataRef.current.spaceName,
							}),
						})
						.finally(() => {
							setCurrentlyScheduledPublishDate(scheduledPublishDate);
						});

					break;
				case 'unscheduled':
					setCurrentlyScheduledPublishDate(null);
					break;
				case 'published':
					void getSessionData().then(({ userId }) => {
						if (accountId === userId) {
							void showPublishSuccessFlags({
								isFirstTimePublish: isUnpublishedDraft,
								shouldTimeout: false,
								spaceName: flagDataRef.current.spaceName,
								flags,
								title: flagDataRef.current.title,
								intl,
							});
						}
					});
			}
		},
		[getFlagMessage, isUnpublishedDraft, intl, flags, isSchedulePublishEnabled],
	);

	useSubscriptionEvent({
		contentId,
		contentType,
		subscriberId: 'useSchedulePublishFlagsWatcher',
		delta: 'schedulePublished',
		onDataReceived: handleSchedulePublish,
	});

	useEffect(() => {
		if (!isSchedulePublishEnabled) {
			return;
		}

		const params = {
			contentId,
			status: [isUnpublishedDraft ? 'draft' : 'current'],
		};
		getContent({ variables: params });
	}, [getContent, contentId, isUnpublishedDraft, isSchedulePublishEnabled]);

	useEffect(() => {
		if (!isSchedulePublishEnabled) {
			return;
		}
		const contentSchedulePublishDate = contentData?.content?.nodes?.[0]?.schedulePublishDate;
		const date = contentSchedulePublishDate ? new Date(contentSchedulePublishDate) : null;
		setCurrentlyScheduledPublishDate(date);
		// The spaceName used to be set here, but it was sometimes invalid.
		// Please re-add calling `setSpaceName` here after GENI-2960 is completed.
	}, [contentData, isSchedulePublishEnabled]);

	useEffect(() => {
		flagDataRef.current = {
			spaceName,
			title,
			isUnpublishedDraft,
			currentlyScheduledPublishDate,
		};
	}, [spaceName, title, isUnpublishedDraft, currentlyScheduledPublishDate]);
};
